import { type FC, type ReactNode, useEffect } from "react";
import { Outlet, useNavigate } from "react-router-dom";
import _ from "lodash";
import { Box } from "@mui/material";
import { UnsupportedChainIdError, useWeb3React } from "@web3-react/core";
import { CHAIN_NAMESPACES, WEB3AUTH_NETWORK } from "@web3auth/no-modal";
import { Web3Auth } from "@web3auth/modal";
import { injected } from "../../connectors";
import { useWeb3AuthContext } from "../../contexts/web3Auth";
import { getSearchParams } from "../../utils";
import Footer from '../footer/Footer';
import Header from "../header/Header";

import "../walletLayout/WalletLayout.css";

interface WalletLayoutProps {
    role?: string;
    children?: ReactNode;
}

const clientId = process.env.REACT_APP_CLIENT_ID || ""; // get from https://dashboard.web3auth.io

const chainConfig = {
    chainNamespace: CHAIN_NAMESPACES.EIP155,
    chainId: process.env.REACT_APP_CHAIN_ID || '',
    rpcTarget: process.env.REACT_APP_RPC_TARGET || '',
    displayName: process.env.REACT_APP_DISPLAY_NAME || 'sixnet',
    ticker: "tSIX",
    tickerName: "SIX",
    blockExplorerUrl: 'https://v2.sixscan.io/',
    logo: '',
};

const web3auth = new Web3Auth({
    clientId,
    web3AuthNetwork: WEB3AUTH_NETWORK.SAPPHIRE_DEVNET,
    chains: [chainConfig],
    defaultChainId: process.env.REACT_APP_CHAIN_ID || '',
});

const WalletLayout: FC<WalletLayoutProps> = ({ role, children }) => {
    const navigate = useNavigate();
    const { activate, account, active } = useWeb3React();
    const roleParam = getSearchParams("role")!;
    const { setWeb3AuthAction } = useWeb3AuthContext()

    const handleConnectWallet = async () => {
        const { ethereum } = window;

        if (!ethereum) {
            alert("MetaMask not install");
            return;
        }

        activate(injected, undefined, true)
            .then(async () => {
                // eslint-disable-next-line
                const walletAddress = await injected.getAccount();
                const getAddress = localStorage.getItem("address");

                if (getAddress !== walletAddress) {
                    localStorage.removeItem("address");
                    localStorage.removeItem("_s");
                }
                localStorage.setItem("walletConnected", "connected");
            })
            .catch((error) => {
                if (error instanceof UnsupportedChainIdError) {
                    activate(injected);
                }
            });
    };

    useEffect(() => {
        const connectWalletOnPageLoad = async () => {
            if (localStorage?.getItem("walletConnected") === "connected") {
                try {
                    await handleConnectWallet();
                    localStorage.setItem("walletConnected", "connected");
                } catch (ex) {
                    localStorage.setItem("walletConnected", "disconnect");
                }
            } else {
                const isIssuer = ["/issuer", "/issuer/total-supply"];
                const isAdmin = ["/admin", "/admin/update-holder", "/admin/change-contract-owner"];

                if ((_.includes(isIssuer, window.location.pathname) ||
                    _.includes(isAdmin, window.location.pathname)
                ) &&
                    active === false &&
                    _.isEmpty(account)) {
                    navigate("/");
                }
            }
        };

        if (role === "issuer" || role === "admin" || role === "tokenAction") {
            connectWalletOnPageLoad();
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    useEffect(() => {
        const init = async () => {
            try {
                // const openloginAdapter = new OpenloginAdapter({
                //     privateKeyProvider: privateKeyProvider,
                // });
                // web3auth.configureAdapter(openloginAdapter);
                await web3auth.init();
                setWeb3AuthAction((prevState: any) => ({
                    ...prevState,
                    web3auth: web3auth
                }));
                if (web3auth.provider) {
                    setWeb3AuthAction((prevState: any) => ({
                        ...prevState,
                        provider: web3auth.provider
                    }));
                }
            } catch (error) {
            }
        };
        if (role === "controller") {
            console.log("init")
            init();
        }
    }, [])

    useEffect(() => {
        const isController = ["/controller", "/controller/new-holder/upload-file", "/controller/update-balance/upload-file"];

        if (_.includes(isController, window.location.pathname) && _.isEmpty(localStorage.getItem("_address"))) {
            navigate("/");
        }
    }, [])

    return (
        <div className="page-container">
            <Header role={window.location.pathname === "/status" ? roleParam : role} />
            <div className="main-content">
                <Box>{children || <Outlet />}</Box>
            </div>
            {/* <Footer /> */}
        </div>
    );
};

export default WalletLayout;
